// Port of Networking/ConnectivityMonitor.swift: app-wide connectivity from `navigator.onLine` and the window's
// `online` / `offline` events (the browser's equivalent of NWPathMonitor), with the manual offline mode.
//
// - `forceOffline` simulates no network (Settings "Offline mode"). While forced, `status` is offline and `isOnline` is
//   false; `pathStatus` keeps reporting the browser's view.
// - `unknown` (before `start()`, after `stop()`) is not offline: requests are attempted and the network reports the
//   real outcome, so a monitor that was never started cannot silently block all networking.
// - Listeners are notified synchronously, once per real change (no framework dependency; the UI wraps it in a signal).

import {
  connectivityStatusFrom,
  sameConnectivityStatus,
  type ConnectivityProviding,
  type ConnectivityStatus,
  type NavigatorConnectivity,
} from './connectivity';

/** Which observable properties changed in a transition. */
export type ConnectivityChange = 'status' | 'pathStatus' | 'forceOffline';

export type ConnectivityListener = (monitor: ConnectivityMonitor, changes: ReadonlySet<ConnectivityChange>) => void;

export interface ConnectivityMonitorOptions {
  forceOffline?: boolean;
  /** Defaults to `globalThis.navigator`. */
  navigator?: NavigatorConnectivity;
  /** Receives `online` / `offline` events; defaults to `globalThis` (the window). */
  eventTarget?: EventTarget;
}

const offline: ConnectivityStatus = { kind: 'offline' };
const unknown: ConnectivityStatus = { kind: 'unknown' };

export class ConnectivityMonitor implements ConnectivityProviding {
  private pathState: ConnectivityStatus = unknown;
  private forced: boolean;
  private readonly listeners = new Set<ConnectivityListener>();
  private readonly nav?: NavigatorConnectivity;
  private readonly target?: EventTarget;
  private monitoring = false;
  private readonly handleEvent = () => this.readPath();

  constructor(options: ConnectivityMonitorOptions = {}) {
    this.forced = options.forceOffline ?? false;
    this.nav = options.navigator ?? (globalThis.navigator as NavigatorConnectivity | undefined);
    this.target = options.eventTarget ?? (typeof globalThis.addEventListener === 'function' ? globalThis : undefined);
  }

  /** Effective status: offline while `forceOffline` is on, otherwise the browser's status. */
  get status(): ConnectivityStatus {
    return this.forced ? offline : this.pathState;
  }

  /** Status reported by the browser, ignoring `forceOffline`. */
  get pathStatus(): ConnectivityStatus {
    return this.pathState;
  }

  /** False when forced offline or when the browser reports no network; true otherwise (including `unknown`). */
  get isOnline(): boolean {
    return this.status.kind !== 'offline';
  }

  /** Simulated offline mode. Takes effect immediately for every reader. */
  get forceOffline(): boolean {
    return this.forced;
  }

  set forceOffline(value: boolean) {
    this.publish(this.mutate(() => (this.forced = value)));
  }

  /** Whether the monitor listens to the browser. */
  get isMonitoring(): boolean {
    return this.monitoring;
  }

  /** Starts listening to `online` / `offline` (and Network Information `change`) events. Idempotent. */
  start(): void {
    if (this.monitoring) return;
    this.monitoring = true;
    this.target?.addEventListener('online', this.handleEvent);
    this.target?.addEventListener('offline', this.handleEvent);
    this.nav?.connection?.addEventListener?.('change', this.handleEvent);
    this.readPath();
  }

  /** Stops listening. The path status returns to `unknown`. */
  stop(): void {
    if (this.monitoring) {
      this.target?.removeEventListener('online', this.handleEvent);
      this.target?.removeEventListener('offline', this.handleEvent);
      this.nav?.connection?.removeEventListener?.('change', this.handleEvent);
      this.monitoring = false;
    }
    this.publish(this.mutate(() => (this.pathState = unknown)));
  }

  /** Calls `listener` after every change; returns the unsubscribe function. */
  subscribe(listener: ConnectivityListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Applies a status directly (tests, previews). Returns what changed. */
  applyPathStatus(status: ConnectivityStatus): ReadonlySet<ConnectivityChange> {
    const changes = this.mutate(() => (this.pathState = status));
    this.publish(changes);
    return changes;
  }

  private readPath(): void {
    if (!this.nav) return;
    this.applyPathStatus(connectivityStatusFrom(this.nav));
  }

  private mutate(body: () => void): Set<ConnectivityChange> {
    const beforeStatus = this.status;
    const beforePath = this.pathState;
    const beforeForced = this.forced;
    body();
    const changes = new Set<ConnectivityChange>();
    if (!sameConnectivityStatus(beforeStatus, this.status)) changes.add('status');
    if (!sameConnectivityStatus(beforePath, this.pathState)) changes.add('pathStatus');
    if (beforeForced !== this.forced) changes.add('forceOffline');
    return changes;
  }

  private publish(changes: ReadonlySet<ConnectivityChange>): void {
    if (changes.size === 0) return;
    for (const listener of [...this.listeners]) {
      try {
        listener(this, changes);
      } catch (error) {
        console.error('Connectivity listener failed', error);
      }
    }
  }
}
